/**
 * Code128 для этикеток.
 *
 * <p>Свой кодировщик, а не библиотека: рисовать надо ровно то, что читает наш
 * же сканер, и управлять шириной модуля с зоной покоя приходится вручную —
 * термопринтер печатает точками, и полоса шириной в полторы точки размазывается
 * в нечитаемую. Риск ручной таблицы закрыт обратной проверкой: тест кодирует
 * строку и декодирует её тем же ZXing, который стоит в сканере.
 *
 * <p><b>Только набор B.</b> Он покрывает всю печатную латиницу, а коды ячеек
 * и деталей короткие: экономия набора C на цифрах дала бы пару миллиметров
 * ценой переключений между наборами — то есть ценой самой частой ошибки
 * в реализациях Code128.
 */

/**
 * Ширины полос для значений 0…106: шесть цифр на символ, начиная с чёрной.
 * Последний — стоп-символ, он на две полосы длиннее.
 */
const PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212',
  '221213', '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221',
  '223211', '221132', '221231', '213212', '223112', '312131', '311222', '321122', '321221',
  '312212', '322112', '322211', '212123', '212321', '232121', '111323', '131123', '131321',
  '112313', '132113', '132311', '211313', '231113', '231311', '112133', '112331', '132131',
  '113123', '113321', '133121', '313121', '211331', '231131', '213113', '213311', '213131',
  '311123', '311321', '331121', '312113', '312311', '332111', '314111', '221411', '431111',
  '111224', '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111', '111242',
  '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311',
  '113141', '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

const START_B = 104;
const STOP = 106;

/**
 * Зона покоя — десять модулей с каждой стороны.
 *
 * <p>Входит в модель, а не остаётся на совести рисующего: без неё сканер
 * не находит начало кода, и выглядит это как «этикетка не читается»,
 * а не как «слишком узкое поле».
 */
export const QUIET_ZONE = 10;

/** Чёрная полоса: смещение и ширина в модулях. */
export interface Bar {
  x: number;
  width: number;
}

export interface Barcode {
  /** Полная ширина в модулях, вместе с зонами покоя. */
  width: number;
  bars: Bar[];
}

/**
 * Кодирует строку.
 *
 * @throws если строка пуста или содержит символы вне набора B (кириллицу,
 *         например): молча выкинуть символ значит напечатать этикетку,
 *         которая отсканируется во что-то другое
 */
export function code128(value: string): Barcode {
  if (value.length === 0) {
    throw new Error('Пустой код не кодируется');
  }

  const values: number[] = [START_B];
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code < 32 || code > 126) {
      throw new Error(
        `Символ «${char}» не кодируется Code128: допустимы только печатные ASCII`,
      );
    }
    values.push(code - 32);
  }

  // Контрольная сумма: старт плюс каждый символ на его позицию, по модулю 103.
  let checksum = START_B;
  for (let at = 1; at < values.length; at++) {
    checksum += values[at]! * at;
  }
  values.push(checksum % 103);
  values.push(STOP);

  const bars: Bar[] = [];
  let x = QUIET_ZONE;
  for (const symbol of values) {
    const pattern = PATTERNS[symbol]!;
    for (let i = 0; i < pattern.length; i++) {
      const width = Number(pattern[i]);
      // Чётные позиции — чёрные полосы, нечётные — пробелы.
      if (i % 2 === 0) {
        bars.push({ x, width });
      }
      x += width;
    }
  }

  return { width: x + QUIET_ZONE, bars };
}
